import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Database, RefreshCw, CheckCircle, AlertTriangle, XCircle,
  ArrowDownUp, Shield, Server, Loader2, ChevronDown, ChevronUp,
  Eye, Zap, Activity, Lock, FileCheck, BarChart3, CircleDot,
  ArrowRight, Snowflake
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function StatusBadge({ status }) {
  const styles = {
    match: "bg-emerald-50 text-emerald-700 border-emerald-200",
    green: "bg-emerald-50 text-emerald-700 border-emerald-200",
    clean: "bg-emerald-50 text-emerald-700 border-emerald-200",
    eligible: "bg-emerald-50 text-emerald-700 border-emerald-200",
    SAFE_TO_CUTOVER: "bg-emerald-50 text-emerald-700 border-emerald-200",
    COMPLETE: "bg-emerald-50 text-emerald-700 border-emerald-200",
    partial: "bg-amber-50 text-amber-700 border-amber-200",
    yellow: "bg-amber-50 text-amber-700 border-amber-200",
    drift_detected: "bg-amber-50 text-amber-700 border-amber-200",
    INSUFFICIENT_DATA: "bg-gray-50 text-gray-500 border-gray-200",
    not_eligible: "bg-red-50 text-red-700 border-red-200",
    mismatch: "bg-red-50 text-red-700 border-red-200",
    empty: "bg-red-50 text-red-700 border-red-200",
    red: "bg-red-50 text-red-700 border-red-200",
    critical: "bg-red-50 text-red-700 border-red-200",
    NOT_READY: "bg-red-50 text-red-700 border-red-200",
    never_run: "bg-gray-50 text-gray-500 border-gray-200",
  };
  return (
    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase border ${styles[status] || styles.never_run}`}
      data-testid={`status-badge-${status}`}>
      {status?.replace(/_/g, " ")}
    </span>
  );
}

function MetricCard({ label, value, sub, icon: Icon, color = "emerald" }) {
  const colors = {
    emerald: "text-emerald-600 bg-emerald-50",
    amber: "text-amber-600 bg-amber-50",
    red: "text-red-600 bg-red-50",
    blue: "text-blue-600 bg-blue-50",
    violet: "text-violet-600 bg-violet-50",
    slate: "text-slate-600 bg-slate-50",
  };
  return (
    <div className="bg-white border border-[#e8e0d0] rounded-lg p-4" data-testid={`metric-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`p-1.5 rounded ${colors[color]}`}>
          <Icon className="h-3.5 w-3.5" />
        </div>
        <span className="text-[10px] font-semibold uppercase tracking-wider text-[#999]">{label}</span>
      </div>
      <div className="text-xl font-bold text-[#1a1a1a]">{value}</div>
      {sub && <div className="text-[11px] text-[#888] mt-0.5">{sub}</div>}
    </div>
  );
}

function CollapsibleSection({ title, icon: Icon, expanded, onToggle, action, children, highlight }) {
  return (
    <div className={`bg-white border rounded-lg overflow-hidden ${highlight ? "border-emerald-300 ring-1 ring-emerald-100" : "border-[#e8e0d0]"}`}
      data-testid={`section-${title.toLowerCase().replace(/\s/g, "-")}`}>
      <button onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#faf7f2] transition-colors text-left">
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-emerald-600" />
          <span className="text-xs font-bold text-[#1a1a1a]">{title}</span>
        </div>
        <div className="flex items-center gap-2">
          {action}
          {expanded ? <ChevronUp className="h-3.5 w-3.5 text-[#999]" /> : <ChevronDown className="h-3.5 w-3.5 text-[#999]" />}
        </div>
      </button>
      {expanded && <div className="px-4 pb-4 border-t border-[#f0ebe0] pt-3">{children}</div>}
    </div>
  );
}

function ConditionRow({ condition }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#f0ebe0] last:border-0"
      data-testid={`gate-condition-${condition.name}`}>
      <div className="flex items-center gap-2">
        {condition.pass ? (
          <CheckCircle className="h-3.5 w-3.5 text-emerald-600" />
        ) : (
          <XCircle className="h-3.5 w-3.5 text-red-500" />
        )}
        <span className="text-xs font-medium text-[#333]">{condition.description}</span>
      </div>
      <div className="text-[10px] text-[#888] max-w-[50%] text-right">{condition.detail}</div>
    </div>
  );
}

export default function MigrationDashboard() {
  const [overview, setOverview] = useState(null);
  const [backfill, setBackfill] = useState(null);
  const [health, setHealth] = useState(null);
  const [drift, setDrift] = useState(null);
  const [validation, setValidation] = useState(null);
  const [rollback, setRollback] = useState(null);
  const [shadowHealth, setShadowHealth] = useState(null);
  const [canaryHealth, setCanaryHealth] = useState(null);
  const [safeCutover, setSafeCutover] = useState(null);
  const [ledgerRecon, setLedgerRecon] = useState(null);
  const [cutoverGate, setCutoverGate] = useState(null);
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningDrift, setRunningDrift] = useState(false);
  const [runningRecon, setRunningRecon] = useState(false);
  const [expandedSection, setExpandedSection] = useState("status");

  const token = localStorage.getItem("ic_token");
  const headers = { Authorization: `Bearer ${token}` };
  const base = `${API}/admin/migration/postgres`;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, bf, hl, dr, vl, rb, sh, ch, sc, lr, cg, ms] = await Promise.all([
        axios.get(`${base}/overview`, { headers }),
        axios.get(`${base}/backfill`, { headers }),
        axios.get(`${base}/dual-write-health`, { headers }),
        axios.get(`${base}/drift`, { headers }),
        axios.get(`${base}/validation`, { headers }),
        axios.get(`${base}/rollback`, { headers }),
        axios.get(`${base}/shadow-read-health`, { headers }),
        axios.get(`${base}/canary-read-health`, { headers }),
        axios.get(`${base}/safe-cutover`, { headers }),
        axios.get(`${base}/ledger-reconciliation`, { headers }),
        axios.get(`${base}/cutover-gate`, { headers }),
        axios.get(`${base}/migration-status`, { headers }),
      ]);
      setOverview(ov.data); setBackfill(bf.data); setHealth(hl.data);
      setDrift(dr.data); setValidation(vl.data); setRollback(rb.data);
      setShadowHealth(sh.data); setCanaryHealth(ch.data); setSafeCutover(sc.data);
      setLedgerRecon(lr.data); setCutoverGate(cg.data); setMigrationStatus(ms.data);
    } catch (err) {
      console.error("Migration dashboard fetch error:", err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const runDriftCheck = async () => {
    setRunningDrift(true);
    try {
      const res = await axios.post(`${base}/drift/run`, {}, { headers });
      setDrift(res.data);
      fetchAll();
    } catch (err) { console.error(err); } finally { setRunningDrift(false); }
  };

  const runReconCheck = async () => {
    setRunningRecon(true);
    try {
      await axios.post(`${base}/ledger-reconciliation/run`, {}, { headers });
      fetchAll();
    } catch (err) { console.error(err); } finally { setRunningRecon(false); }
  };

  const toggle = (s) => setExpandedSection(expandedSection === s ? null : s);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="migration-loading">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
        <span className="ml-2 text-sm text-[#888]">Loading migration status...</span>
      </div>
    );
  }

  const phase = migrationStatus?.phase || overview?.phase || "A_MONGO";
  const stage = migrationStatus?.stage || overview?.migration_stage || 1;
  const isComplete = migrationStatus?.is_complete || false;

  return (
    <div className="space-y-4" data-testid="migration-dashboard">
      {/* Header with Migration Status Banner */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-[#1a1a1a]">PostgreSQL Migration</h2>
          <p className="text-[11px] text-[#888]">{_phaseLabel(phase, stage)}</p>
        </div>
        <button onClick={fetchAll} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-[#e8e0d0] text-xs font-medium text-[#666] hover:bg-[#faf7f2] transition-colors"
          data-testid="migration-refresh-btn">
          <RefreshCw className="h-3 w-3" /> Refresh
        </button>
      </div>

      {/* Migration Status Banner */}
      <div className={`rounded-lg border p-4 ${isComplete
          ? "bg-emerald-50 border-emerald-300"
          : "bg-[#faf7f2] border-[#e8e0d0]"}`}
        data-testid="migration-status-banner">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${isComplete ? "bg-emerald-100" : "bg-white border border-[#e8e0d0]"}`}>
              {isComplete
                ? <CheckCircle className="h-5 w-5 text-emerald-600" />
                : <Activity className="h-5 w-5 text-amber-600" />}
            </div>
            <div>
              <div className="text-sm font-bold text-[#1a1a1a]" data-testid="migration-status-label">
                {isComplete ? "Migration Complete" : "Phase D In Progress"}
              </div>
              <div className="text-[10px] text-[#888]">
                Stage {stage}/5: {migrationStatus?.stage_description || ""}
              </div>
            </div>
          </div>
          <StatusBadge status={isComplete ? "COMPLETE" : phase} />
        </div>

        {/* Source indicators */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
          <SourceIndicator label="Read Source" value={migrationStatus?.read_source || "mongo"}
            pg={migrationStatus?.pg_handles_reads} />
          <SourceIndicator label="Write Source" value={migrationStatus?.write_source || "mongo"}
            pg={migrationStatus?.pg_handles_writes} />
          <FreezeIndicator label="Mongo Writes" frozen={migrationStatus?.mongo_frozen} />
          <RollbackIndicator ready={migrationStatus?.rollback_ready} />
        </div>
      </div>

      {/* Top Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <MetricCard label="Source of Truth" value={overview?.source_of_truth === "mongodb" ? "MongoDB" : "PostgreSQL"}
          sub={`Stage ${stage}`} icon={Database} color="blue" />
        <MetricCard label="Backfill" value={`${backfill?.overall_completion_pct || 0}%`}
          sub={`${backfill?.total_pg || 0}/${backfill?.total_mongo || 0}`} icon={ArrowDownUp}
          color={backfill?.overall_completion_pct >= 95 ? "emerald" : "amber"} />
        <MetricCard label="Dual-Write" value={`${overview?.registry?.dual_write_active || 0}/12`}
          sub="domains" icon={Server} color={overview?.registry?.dual_write_active > 0 ? "emerald" : "blue"} />
        <MetricCard label="Cutover Gate" value={cutoverGate?.eligible ? "Eligible" : "Not Ready"}
          sub={`${cutoverGate?.passed_count || 0}/${cutoverGate?.total_conditions || 7} conditions`}
          icon={Lock} color={cutoverGate?.eligible ? "emerald" : "amber"} />
        <MetricCard label="Ledger Recon" value={ledgerRecon?.all_clean_7d ? "Clean" : ledgerRecon?.latest_run ? "Issues" : "No Runs"}
          sub={`Streak: ${ledgerRecon?.clean_streak || 0} runs`} icon={BarChart3}
          color={ledgerRecon?.all_clean_7d ? "emerald" : ledgerRecon?.latest_run?.has_mismatch ? "red" : "slate"} />
      </div>

      {/* Pre-Cutover Gate */}
      <CollapsibleSection title="Pre-Cutover Verification Gate" icon={Lock}
        expanded={expandedSection === "gate"} onToggle={() => toggle("gate")} highlight={true}>
        {cutoverGate ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 rounded-lg border"
              style={{ background: cutoverGate.eligible ? "#f0fdf4" : "#fef2f2",
                borderColor: cutoverGate.eligible ? "#86efac" : "#fca5a5" }}
              data-testid="cutover-gate-status">
              {cutoverGate.eligible
                ? <CheckCircle className="h-5 w-5 text-emerald-600" />
                : <XCircle className="h-5 w-5 text-red-500" />}
              <div>
                <div className="text-sm font-bold" style={{ color: cutoverGate.eligible ? "#15803d" : "#b91c1c" }}>
                  {cutoverGate.eligible ? "Cutover Eligible" : "Cutover Not Eligible"}
                </div>
                <div className="text-[10px] text-[#888]">
                  {cutoverGate.passed_count}/{cutoverGate.total_conditions} conditions met
                  {cutoverGate.last_passing_timestamp && ` | Last eligible: ${new Date(cutoverGate.last_passing_timestamp).toLocaleString()}`}
                </div>
              </div>
            </div>
            <div className="space-y-1">
              {cutoverGate.conditions?.map((c) => (
                <ConditionRow key={c.name} condition={c} />
              ))}
            </div>
            {cutoverGate.eligible && migrationStatus?.next_transition?.description && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg mt-2">
                <div className="text-[10px] font-semibold uppercase text-emerald-700 mb-1">Next Transition Available</div>
                <div className="flex items-center gap-2 text-xs text-emerald-800">
                  <ArrowRight className="h-3 w-3" />
                  {migrationStatus.next_transition.description}
                </div>
                <div className="text-[10px] text-emerald-600 mt-1">Manual env change required — cutover is never automatic</div>
              </div>
            )}
          </div>
        ) : <div className="text-xs text-[#999] text-center py-4">No gate data.</div>}
      </CollapsibleSection>

      {/* Ledger Reconciliation */}
      <CollapsibleSection title="Ledger Reconciliation Health" icon={BarChart3}
        expanded={expandedSection === "recon"} onToggle={() => toggle("recon")}
        action={
          <button onClick={runReconCheck} disabled={runningRecon}
            className="flex items-center gap-1 px-2 py-1 rounded border border-emerald-200 text-[10px] font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 transition-colors"
            data-testid="run-recon-btn">
            {runningRecon ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Run Check
          </button>
        }>
        {ledgerRecon ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-2 bg-[#faf7f2] rounded text-center border border-[#e8e0d0]">
                <div className="text-lg font-bold text-[#1a1a1a]">{ledgerRecon.clean_streak || 0}</div>
                <div className="text-[10px] text-[#888]">Clean Streak</div>
              </div>
              <div className="p-2 bg-[#faf7f2] rounded text-center border border-[#e8e0d0]">
                <div className="text-lg font-bold text-[#1a1a1a]">{ledgerRecon.total_runs_7d || 0}</div>
                <div className="text-[10px] text-[#888]">7-Day Runs</div>
              </div>
              <div className={`p-2 rounded text-center border ${ledgerRecon.all_clean_7d ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}>
                <div className={`text-lg font-bold ${ledgerRecon.all_clean_7d ? "text-emerald-700" : "text-red-700"}`}>
                  {ledgerRecon.all_clean_7d ? "Yes" : "No"}
                </div>
                <div className="text-[10px] text-[#888]">All Clean 7d</div>
              </div>
              <div className={`p-2 rounded text-center border ${ledgerRecon.clean_for_7_days ? "bg-emerald-50 border-emerald-200" : "bg-gray-50 border-gray-200"}`}>
                <div className={`text-lg font-bold ${ledgerRecon.clean_for_7_days ? "text-emerald-700" : "text-gray-500"}`}>
                  {ledgerRecon.clean_for_7_days ? "PASS" : "Pending"}
                </div>
                <div className="text-[10px] text-[#888]">7-Day Gate</div>
              </div>
            </div>

            {/* Latest run details */}
            {ledgerRecon.latest_run && (
              <div className="space-y-2 pt-2 border-t border-[#f0ebe0]">
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-[#888]">Latest Run:</span>
                  <StatusBadge status={ledgerRecon.latest_run.status} />
                  <span className="text-[#888] text-[10px]">{new Date(ledgerRecon.latest_run.run_at).toLocaleString()}</span>
                </div>
                {ledgerRecon.latest_run.domains?.map((d) => (
                  <div key={d.domain} className="flex items-center justify-between py-1.5 border-b border-[#f0ebe0] last:border-0"
                    data-testid={`recon-domain-${d.domain}`}>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-[#333] w-28">{d.domain}</span>
                      <StatusBadge status={d.status} />
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-[#888]">
                      {d.checks?.count && (
                        <span className={d.checks.count.pass ? "text-emerald-600" : "text-red-600"}>
                          COUNT: {d.checks.count.pass ? "Match" : `delta ${d.checks.count.delta}`}
                        </span>
                      )}
                      {d.checks?.sum_amount && (
                        <span className={d.checks.sum_amount.pass ? "text-emerald-600" : "text-red-600"}>
                          SUM: {d.checks.sum_amount.pass ? "Match" : `delta ${d.checks.sum_amount.delta}`}
                        </span>
                      )}
                      {d.checks?.balance_checksum && (
                        <span className={d.checks.balance_checksum.pass ? "text-emerald-600" : "text-red-600"}>
                          CHKSUM: {d.checks.balance_checksum.pass ? "Match" : "Mismatch"}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* State timestamps */}
            <div className="grid grid-cols-2 gap-3 text-xs pt-2 border-t border-[#f0ebe0]">
              <div>
                <span className="text-[#888]">Last Success:</span>{" "}
                <span className="font-medium">
                  {ledgerRecon.last_successful_run
                    ? new Date(ledgerRecon.last_successful_run).toLocaleString()
                    : "Never"}
                </span>
              </div>
              <div>
                <span className="text-[#888]">Last Failure:</span>{" "}
                <span className={`font-medium ${ledgerRecon.last_failed_run ? "text-red-600" : ""}`}>
                  {ledgerRecon.last_failed_run
                    ? new Date(ledgerRecon.last_failed_run).toLocaleString()
                    : "Never"}
                </span>
              </div>
            </div>
          </div>
        ) : <div className="text-xs text-[#999] text-center py-4">No reconciliation data.</div>}
      </CollapsibleSection>

      {/* Overview */}
      <CollapsibleSection title="Overview" icon={Database} expanded={expandedSection === "overview"} onToggle={() => toggle("overview")}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
          <div className="space-y-2">
            <div className="flex justify-between"><span className="text-[#888]">Phase</span><span className="font-medium">{phase}</span></div>
            <div className="flex justify-between"><span className="text-[#888]">Stage</span><span className="font-medium">{stage}/5</span></div>
            <div className="flex justify-between"><span className="text-[#888]">Backend</span><span className="font-medium">{overview?.registry?.backend}</span></div>
            <div className="flex justify-between"><span className="text-[#888]">PG Available</span>
              <span className={overview?.postgres_available ? "text-emerald-600 font-medium" : "text-red-600 font-medium"}>
                {overview?.postgres_available ? "Yes" : "No"}
              </span>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between"><span className="text-[#888]">Read Source</span><span className="font-medium">{overview?.registry?.read_source}</span></div>
            <div className="flex justify-between"><span className="text-[#888]">Write Source</span><span className="font-medium">{overview?.registry?.write_source}</span></div>
            <div className="flex justify-between"><span className="text-[#888]">Mongo Writes</span>
              <span className={overview?.registry?.mongo_write_enabled ? "text-emerald-600 font-medium" : "text-blue-600 font-medium"}>
                {overview?.registry?.mongo_write_enabled ? "Enabled" : "Frozen"}
              </span>
            </div>
            <div className="flex justify-between"><span className="text-[#888]">Fallback</span><span className="font-medium">{overview?.registry?.read_fallback_enabled ? "Enabled" : "Disabled"}</span></div>
          </div>
          <div className="space-y-2">
            <div className="flex justify-between"><span className="text-[#888]">Dual-Write</span><span className="font-medium">{overview?.registry?.dual_write_active}/12</span></div>
            <div className="flex justify-between"><span className="text-[#888]">Shadow Reads</span><span className="font-medium">{overview?.registry?.shadow_read_active}/12</span></div>
            <div className="flex justify-between"><span className="text-[#888]">Sample Rate</span><span className="font-medium">{(overview?.registry?.shadow_read_sample_rate || 0) * 100}%</span></div>
            <div className="flex justify-between"><span className="text-[#888]">Canary %</span><span className="font-medium">{(overview?.registry?.canary_read_percent || 0) * 100}%</span></div>
          </div>
        </div>
      </CollapsibleSection>

      {/* Backfill */}
      <CollapsibleSection title="Backfill Status" icon={ArrowDownUp} expanded={expandedSection === "backfill"} onToggle={() => toggle("backfill")}>
        <div className="space-y-2">
          {backfill?.domains?.map((d) => (
            <div key={d.domain} className="flex items-center justify-between py-1.5 border-b border-[#f0ebe0] last:border-0" data-testid={`backfill-domain-${d.domain}`}>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-[#333] w-32">{d.domain}</span>
                <StatusBadge status={d.status} />
              </div>
              <div className="flex items-center gap-3 text-xs text-[#888]">
                <span>{d.pg_count}/{d.mongo_count}</span>
                <div className="w-20 bg-[#f0ebe0] rounded-full h-1.5">
                  <div className="bg-emerald-500 h-1.5 rounded-full transition-all" style={{ width: `${Math.min(d.completion_pct, 100)}%` }} />
                </div>
                <span className="w-10 text-right font-medium">{d.completion_pct}%</span>
              </div>
            </div>
          ))}
        </div>
      </CollapsibleSection>

      {/* Dual-Write Health */}
      <CollapsibleSection title="Dual-Write Health" icon={Server} expanded={expandedSection === "health"} onToggle={() => toggle("health")}>
        {health?.domain_health?.length > 0 ? (
          <div className="space-y-2">
            {health.domain_health.map((d) => (
              <div key={d.domain} className="flex items-center justify-between py-1.5 border-b border-[#f0ebe0] last:border-0" data-testid={`dw-health-${d.domain}`}>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-[#333] w-32">{d.domain}</span>
                  <StatusBadge status={d.status} />
                </div>
                <div className="flex items-center gap-4 text-xs text-[#888]">
                  <span>{d.success}/{d.total_writes} writes</span>
                  <span className={d.success_rate_pct >= 99 ? "text-emerald-600 font-medium" : "text-red-600 font-medium"}>{d.success_rate_pct}%</span>
                  <span>{d.avg_latency_ms}ms</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-[#999] text-center py-4">No dual-write domains active.</div>
        )}
      </CollapsibleSection>

      {/* Shadow Read Health */}
      <CollapsibleSection title="Shadow Read Health" icon={Eye} expanded={expandedSection === "shadow"} onToggle={() => toggle("shadow")}>
        {shadowHealth?.domain_health?.length > 0 ? (
          <div className="space-y-2">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <div className="p-2 bg-[#faf7f2] rounded text-center">
                <div className="text-lg font-bold text-[#1a1a1a]">{shadowHealth.sample_rate * 100}%</div>
                <div className="text-[10px] text-[#888]">Sample Rate</div>
              </div>
              <div className="p-2 bg-[#faf7f2] rounded text-center">
                <div className="text-lg font-bold text-emerald-600">{shadowHealth.severity_counts?.CRITICAL || 0}</div>
                <div className="text-[10px] text-[#888]">Critical Mismatches</div>
              </div>
              <div className="p-2 bg-[#faf7f2] rounded text-center">
                <div className="text-lg font-bold text-[#1a1a1a]">{shadowHealth.shadow_read_active}</div>
                <div className="text-[10px] text-[#888]">Active Domains</div>
              </div>
            </div>
            {shadowHealth.domain_health.map((d) => (
              <div key={d.domain} className="flex items-center justify-between py-1.5 border-b border-[#f0ebe0] last:border-0" data-testid={`shadow-health-${d.domain}`}>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-[#333] w-32">{d.domain}</span>
                  <StatusBadge status={d.status} />
                </div>
                <div className="flex items-center gap-4 text-xs text-[#888]">
                  <span>{d.matches}/{d.total_verified} matched</span>
                  <span className={d.match_rate_pct >= 99.9 ? "text-emerald-600 font-medium" : "text-red-600 font-medium"}>
                    {d.match_rate_pct}%
                  </span>
                  <span>{d.avg_pg_latency_ms}ms</span>
                </div>
              </div>
            ))}
            {shadowHealth.recent_mismatches?.length > 0 && (
              <div className="mt-3 pt-3 border-t border-[#f0ebe0]">
                <h4 className="text-[10px] font-semibold uppercase text-red-600 mb-2">Recent Mismatches</h4>
                {shadowHealth.recent_mismatches.slice(-5).map((m, i) => (
                  <div key={i} className="text-[10px] text-[#888] py-1 border-b border-[#f5f0e8] last:border-0">
                    <span className={m.severity === "CRITICAL" ? "text-red-600 font-bold" : "text-amber-600 font-medium"}>[{m.severity}]</span>{" "}
                    {m.domain}.{m.method}: {m.fields?.join(", ")}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs text-[#999] text-center py-4">No shadow read domains active. Set SHADOW_READ_DOMAINS to enable.</div>
        )}
      </CollapsibleSection>

      {/* Canary Read Health */}
      <CollapsibleSection title="PG Read Health (Canary)" icon={Zap} expanded={expandedSection === "canary"} onToggle={() => toggle("canary")}>
        {canaryHealth?.domain_health?.length > 0 ? (
          <div className="space-y-2">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <div className="p-2 bg-[#faf7f2] rounded text-center">
                <div className="text-lg font-bold text-[#1a1a1a]">{canaryHealth.canary_percent * 100}%</div>
                <div className="text-[10px] text-[#888]">Canary %</div>
              </div>
              <div className="p-2 bg-[#faf7f2] rounded text-center">
                <div className="text-lg font-bold text-[#1a1a1a]">{canaryHealth.read_fallback_enabled ? "ON" : "OFF"}</div>
                <div className="text-[10px] text-[#888]">Fallback</div>
              </div>
              <div className="p-2 bg-[#faf7f2] rounded text-center">
                <div className="text-lg font-bold text-[#1a1a1a]">{canaryHealth.canary_active}</div>
                <div className="text-[10px] text-[#888]">Active Domains</div>
              </div>
            </div>
            {canaryHealth.domain_health.map((d) => (
              <div key={d.domain} className="flex items-center justify-between py-1.5 border-b border-[#f0ebe0] last:border-0" data-testid={`canary-health-${d.domain}`}>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-[#333] w-32">{d.domain}</span>
                  <StatusBadge status={d.status} />
                </div>
                <div className="flex items-center gap-4 text-xs text-[#888]">
                  <span>{d.pg_success}/{d.pg_reads} PG reads</span>
                  <span className={d.pg_success_rate_pct >= 99 ? "text-emerald-600 font-medium" : "text-red-600 font-medium"}>
                    {d.pg_success_rate_pct}%
                  </span>
                  <span>{d.avg_pg_latency_ms}ms</span>
                  {d.mongo_fallback > 0 && <span className="text-amber-600">{d.mongo_fallback} fallbacks</span>}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs text-[#999] text-center py-4">No canary read domains active. Set PG_READ_CANARY_DOMAINS to enable.</div>
        )}
      </CollapsibleSection>

      {/* Drift Detection */}
      <CollapsibleSection title="Drift Detection" icon={AlertTriangle} expanded={expandedSection === "drift"} onToggle={() => toggle("drift")}
        action={
          <button onClick={runDriftCheck} disabled={runningDrift}
            className="flex items-center gap-1 px-2 py-1 rounded border border-emerald-200 text-[10px] font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 transition-colors"
            data-testid="run-drift-btn">
            {runningDrift ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
            Run Check
          </button>
        }>
        {drift?.status === "never_run" || !drift?.domains ? (
          <div className="text-xs text-[#999] text-center py-4">Click "Run Check" to execute drift detection.</div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-3 mb-3">
              <StatusBadge status={drift.status} />
              <span className="text-xs text-[#888]">Drift: {drift.total_drift} | Critical: {drift.critical_drift}</span>
            </div>
            {drift.domains?.map((d) => (
              <div key={d.domain} className="flex items-center justify-between py-1.5 border-b border-[#f0ebe0] last:border-0" data-testid={`drift-domain-${d.domain}`}>
                <span className="text-xs font-medium text-[#333] w-32">{d.domain}</span>
                <div className="flex items-center gap-3 text-xs">
                  {d.counts_match ? (
                    <span className="text-emerald-600 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> match</span>
                  ) : (
                    <span className="text-red-600 flex items-center gap-1"><XCircle className="h-3 w-3" /> drift: {d.count_drift}</span>
                  )}
                  {d.money_field_mismatches?.length > 0 && (
                    <span className="text-red-600 font-bold">{d.money_field_mismatches.length} money</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CollapsibleSection>

      {/* Validation */}
      <CollapsibleSection title="Validation Reports" icon={CheckCircle} expanded={expandedSection === "validation"} onToggle={() => toggle("validation")}>
        {validation?.domains ? (
          <div className="space-y-2">
            <div className="mb-3"><StatusBadge status={validation.verdict === "PASS" ? "green" : "critical"} /></div>
            {validation.domains.map((d) => (
              <div key={d.domain} className="flex items-center justify-between py-1.5 border-b border-[#f0ebe0] last:border-0" data-testid={`validation-domain-${d.domain}`}>
                <span className="text-xs font-medium text-[#333] w-32">{d.domain}</span>
                <div className="flex items-center gap-3 text-xs">
                  <span className={d.counts_match ? "text-emerald-600" : "text-red-600"}>
                    Counts: {d.counts_match ? "Match" : `${d.mongo_count}/${d.pg_count}`}
                  </span>
                  {d.money_fields_match !== null && (
                    <span className={d.money_fields_match ? "text-emerald-600" : "text-red-600 font-bold"}>
                      Money: {d.money_fields_match ? "OK" : "MISMATCH"}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : <div className="text-xs text-[#999] text-center py-4">No validation data.</div>}
      </CollapsibleSection>

      {/* Safe Cutover */}
      <CollapsibleSection title="Safe Cutover Status" icon={FileCheck} expanded={expandedSection === "cutover"} onToggle={() => toggle("cutover")}>
        {safeCutover?.domains ? (
          <div className="space-y-2">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
              <div className="p-2 bg-emerald-50 rounded text-center border border-emerald-200">
                <div className="text-lg font-bold text-emerald-700">{safeCutover.safe_count}</div>
                <div className="text-[10px] text-emerald-600">Safe</div>
              </div>
              <div className="p-2 bg-red-50 rounded text-center border border-red-200">
                <div className="text-lg font-bold text-red-700">{safeCutover.not_ready_count}</div>
                <div className="text-[10px] text-red-600">Not Ready</div>
              </div>
              <div className="p-2 bg-gray-50 rounded text-center border border-gray-200">
                <div className="text-lg font-bold text-gray-500">{safeCutover.insufficient_data_count}</div>
                <div className="text-[10px] text-gray-500">Pending</div>
              </div>
            </div>
            {safeCutover.domains.map((d) => (
              <div key={d.domain} className="py-2 border-b border-[#f0ebe0] last:border-0" data-testid={`cutover-domain-${d.domain}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-[#333] w-32">{d.domain}</span>
                  <StatusBadge status={d.verdict} />
                </div>
                <div className="flex flex-wrap gap-2 pl-2">
                  <CutoverCheck label="DW" pass={d.dual_write_active} />
                  <CutoverCheck label="Shadow" pass={d.shadow_read_active} />
                  <CutoverCheck label={`Parity ${d.shadow_read_parity_pct || 0}%`} pass={d.shadow_read_parity_pass} />
                  <CutoverCheck label={`DW ${d.dual_write_success_pct || 0}%`} pass={d.dual_write_success_pass} />
                  <CutoverCheck label="Drift" pass={d.drift_clean} />
                  <CutoverCheck label="Money" pass={d.money_match} />
                  <CutoverCheck label="0 Critical" pass={d.zero_critical_mismatches} />
                </div>
              </div>
            ))}
          </div>
        ) : <div className="text-xs text-[#999] text-center py-4">No cutover data.</div>}
      </CollapsibleSection>

      {/* Rollback */}
      <CollapsibleSection title="Rollback Controls" icon={Shield} expanded={expandedSection === "rollback"} onToggle={() => toggle("rollback")}>
        {rollback && (
          <div className="space-y-3">
            <div className={`flex items-center gap-2 p-3 rounded-lg border ${rollback.rollback_ready
                ? "bg-emerald-50 border-emerald-200"
                : "bg-amber-50 border-amber-200"}`}
              data-testid="rollback-status">
              {rollback.rollback_ready
                ? <CheckCircle className="h-4 w-4 text-emerald-600" />
                : <AlertTriangle className="h-4 w-4 text-amber-600" />}
              <span className={`text-xs font-medium ${rollback.rollback_ready ? "text-emerald-700" : "text-amber-700"}`}>
                {rollback.rollback_ready
                  ? `Rollback Ready — ${rollback.estimated_rollback_time}`
                  : "Migration complete — rollback requires data restore"}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div><span className="text-[#888]">SOT:</span> <span className="font-medium">{rollback.current_source_of_truth}</span></div>
              <div><span className="text-[#888]">Read Source:</span> <span className="font-medium">{rollback.read_source}</span></div>
              <div><span className="text-[#888]">Write Source:</span> <span className="font-medium">{rollback.write_source}</span></div>
              <div><span className="text-[#888]">Mongo Writes:</span>
                <span className={`font-medium ${rollback.mongo_write_enabled ? "text-emerald-600" : "text-blue-600"}`}>
                  {rollback.mongo_write_enabled ? "Enabled" : "Frozen"}
                </span>
              </div>
              <div><span className="text-[#888]">Stage:</span> <span className="font-medium">{rollback.migration_stage}/5</span></div>
              <div><span className="text-[#888]">Risk:</span> <span className="font-medium text-emerald-600">{rollback.data_loss_risk}</span></div>
            </div>
            <div className="p-3 bg-[#faf7f2] rounded-lg border border-[#e8e0d0]">
              <h4 className="text-[10px] font-semibold uppercase text-[#999] mb-2">Rollback Steps (Stage {rollback.migration_stage})</h4>
              {rollback.rollback_steps?.map((step, i) => (
                <div key={i} className="text-[11px] text-[#666] py-0.5">{step}</div>
              ))}
            </div>
          </div>
        )}
      </CollapsibleSection>
    </div>
  );
}

function SourceIndicator({ label, value, pg }) {
  return (
    <div className={`p-2 rounded-lg border text-center ${pg ? "bg-emerald-50 border-emerald-200" : "bg-white border-[#e8e0d0]"}`}
      data-testid={`source-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="text-[10px] text-[#888] uppercase font-semibold">{label}</div>
      <div className={`text-xs font-bold mt-0.5 ${pg ? "text-emerald-700" : "text-[#333]"}`}>
        {value === "postgres" ? "PostgreSQL" : "MongoDB"}
      </div>
    </div>
  );
}

function FreezeIndicator({ label, frozen }) {
  return (
    <div className={`p-2 rounded-lg border text-center ${frozen ? "bg-blue-50 border-blue-200" : "bg-white border-[#e8e0d0]"}`}
      data-testid="mongo-freeze-status">
      <div className="text-[10px] text-[#888] uppercase font-semibold">{label}</div>
      <div className={`text-xs font-bold mt-0.5 flex items-center justify-center gap-1 ${frozen ? "text-blue-700" : "text-emerald-700"}`}>
        {frozen ? <><Snowflake className="h-3 w-3" /> Frozen</> : "Active"}
      </div>
    </div>
  );
}

function RollbackIndicator({ ready }) {
  return (
    <div className={`p-2 rounded-lg border text-center ${ready ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}
      data-testid="rollback-ready-indicator">
      <div className="text-[10px] text-[#888] uppercase font-semibold">Rollback</div>
      <div className={`text-xs font-bold mt-0.5 ${ready ? "text-emerald-700" : "text-amber-700"}`}>
        {ready ? "Ready" : "N/A"}
      </div>
    </div>
  );
}

function CutoverCheck({ label, pass: p }) {
  if (p === null || p === undefined) return (
    <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-50 text-gray-400 border border-gray-200">{label}: —</span>
  );
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${p ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}`}>
      {p ? <CheckCircle className="h-2.5 w-2.5 inline mr-0.5" /> : <XCircle className="h-2.5 w-2.5 inline mr-0.5" />}
      {label}
    </span>
  );
}

function _phaseLabel(phase, stage) {
  const labels = {
    "A_MONGO": "Phase A — MongoDB Primary",
    "B_DUAL_WRITE": "Phase B — Dual-Write Active",
    "C_SHADOW": "Phase C — Shadow Read Verification",
    "C_CANARY": "Phase C — Canary Read Routing",
    "D_PG_READS": "Phase D — PostgreSQL Reads Active",
    "D_PG_PRIMARY": "Phase D — PostgreSQL Primary Writes",
    "D_FROZEN": "Phase D — MongoDB Frozen",
    "COMPLETE": "Migration Complete — PostgreSQL Primary",
  };
  return labels[phase] || `Stage ${stage}/5`;
}
